import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router';
import { MdOutlineMarkEmailRead } from 'react-icons/md';
import AuthPageShell from './AuthPageShell';
import { getEnvUrl } from '../../utils/envUrl';
import '../../styles/common.css';

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setEmail(e.target.value);
    setError('');
  };

  const validate = () => {
    if (!email) {
      return 'Email is required';
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      return 'Email address is invalid';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/users/forgotPassword`, {
        email,
        env_url: getEnvUrl(),
      });

      if (response.data.code === 200) {
        setIsSubmitted(true);
      } else {
        setError('Failed to send reset link. Please try again.');
      }
    } catch (err) {
      console.error('Error sending reset link:', err);
      setError('An error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageShell
      title={isSubmitted ? 'Check Your Email' : 'Forgot Password'}
      subtitle={
        isSubmitted
          ? 'Reset instructions have been sent'
          : "Enter your email and we'll send you a reset link"
      }
      footer={
        <p>
          Remember your password?{' '}
          <button type="button" className="btn-link" onClick={() => navigate('/auth/login')}>
            Back to Sign In
          </button>
        </p>
      }
    >
      {!isSubmitted ? (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="forgot-email" className="form-label">
              Email
            </label>
            <input
              type="email"
              id="forgot-email"
              className="form-input"
              placeholder="name@company.com"
              value={email}
              onChange={handleChange}
              autoComplete="email"
              disabled={isLoading}
            />
            {error ? <div className="form-error">{error}</div> : null}
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={isLoading}>
            {isLoading ? 'Sending…' : 'Send Reset Link'}
          </button>
        </form>
      ) : (
        <div className="auth-steps">
          <div className="auth-step">
            <div className="auth-step__badge auth-step__badge--done">
              <MdOutlineMarkEmailRead size={14} />
            </div>
            <div>
              <p className="auth-step__title">Email sent to</p>
              <div className="auth-step__chip">
                <MdOutlineMarkEmailRead size={12} />
                {email}
              </div>
            </div>
          </div>

          <div className="auth-step">
            <div className="auth-step__badge">2</div>
            <div>
              <p className="auth-step__title">Open the reset link</p>
              <p className="auth-step__text">
                Click the link in the email to set a new password. It expires in 48 hours.
              </p>
            </div>
          </div>

          <div className="auth-step">
            <div className="auth-step__badge">3</div>
            <div>
              <p className="auth-step__title">
                No email?{' '}
                <button
                  type="button"
                  className="btn-link"
                  onClick={() => setIsSubmitted(false)}
                >
                  Resend it
                </button>
              </p>
              <p className="auth-step__text">
                Check your spam folder first, then try resending.
              </p>
            </div>
          </div>
        </div>
      )}
    </AuthPageShell>
  );
};

export default ForgotPassword;
